// Per-project custom field definition management section.
// Rendered inside ProjectSettingsPanel below StatusesSection.
// Features: list (sort_order), add (label/type/required/choices for select types),
// inline-edit, reorder with up/down, delete with confirm.
// Value rendering on tasks is D4-fe-2.

import { useState } from "preact/hooks";
import { ChevronDown, ChevronUp, Plus, Trash2, X } from "lucide-preact";
import type { CustomFieldDef, CustomFieldType } from "@juuplan/shared";
import { CUSTOM_FIELD_TYPES } from "@juuplan/shared";
import { toastSuccess } from "../lib/toast";
import {
  useCustomFields,
  addCustomField,
  patchCustomField,
  reorderCustomFieldDefs,
  removeCustomField,
} from "../store/customFields";
import { useWorkItemTypes } from "../store/workItemTypes";

import { Button } from "./ui/Button";

const TYPE_LABEL: Record<CustomFieldType, string> = {
  text: "Text",
  number: "Zahl",
  date: "Datum",
  select: "Auswahl (einfach)",
  multiselect: "Auswahl (mehrfach)",
  checkbox: "Checkbox",
  user: "User",
  url: "URL",
};

/** Types that require a choices editor. */
const NEEDS_CHOICES: Set<CustomFieldType> = new Set(["select", "multiselect"]);

interface Props {
  projectId: number;
  /** When false the mutating controls are hidden (view-only role). */
  canEdit: boolean;
}

interface EditState {
  label: string;
  type: CustomFieldType;
  required: boolean;
  choices: string[];
  workItemTypeId: number | null;
}

/**
 * Per-project custom field definition management list.
 * Renders inside ProjectSettingsPanel as a section below StatusesSection.
 */
export function CustomFieldsSection(props: Props): preact.JSX.Element {
  const { projectId, canEdit } = props;
  const { items, loading, error } = useCustomFields(projectId);
  const { items: workItemTypes } = useWorkItemTypes(projectId);

  const [editing, setEditing] = useState<Map<number, EditState>>(new Map());
  const [saving, setSaving] = useState<Set<number>>(new Set());
  const [confirmDel, setConfirmDel] = useState<Set<number>>(new Set());

  // New-field form state
  const [showNew, setShowNew] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState<CustomFieldType>("text");
  const [newRequired, setNewRequired] = useState(false);
  const [newChoices, setNewChoices] = useState<string[]>([]);
  const [newChoiceInput, setNewChoiceInput] = useState("");
  const [newWorkItemTypeId, setNewWorkItemTypeId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  // ---- helpers ----

  const startEdit = (f: CustomFieldDef): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.set(f.id, {
        label: f.label,
        type: f.type,
        required: f.required,
        choices: f.options?.choices ?? [],
        workItemTypeId: f.work_item_type_id,
      });
      return next;
    });
  };

  const cancelEdit = (id: number): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const setDraft = (id: number, patch: Partial<EditState>): void => {
    setEditing((prev) => {
      const cur = prev.get(id);
      if (!cur) return prev;
      const next = new Map(prev);
      next.set(id, { ...cur, ...patch });
      return next;
    });
  };

  const commitEdit = async (f: CustomFieldDef): Promise<void> => {
    const draft = editing.get(f.id);
    if (!draft) return;
    setSaving((prev) => new Set(prev).add(f.id));
    try {
      const options =
        NEEDS_CHOICES.has(draft.type) && draft.choices.length > 0
          ? { choices: draft.choices }
          : null;
      await patchCustomField(f.id, projectId, {
        label: draft.label.trim() || f.label,
        type: draft.type,
        required: draft.required,
        options,
        work_item_type_id: draft.workItemTypeId,
      });
      cancelEdit(f.id);
      toastSuccess("Feld gespeichert.");
    } catch {
      // api wrapper already toasted
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(f.id);
        return next;
      });
    }
  };

  const handleDelete = async (f: CustomFieldDef): Promise<void> => {
    setSaving((prev) => new Set(prev).add(f.id));
    try {
      await removeCustomField(f.id, projectId);
      setConfirmDel((prev) => {
        const next = new Set(prev);
        next.delete(f.id);
        return next;
      });
      toastSuccess("Feld gelöscht.");
    } catch {
      // api wrapper already toasted
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(f.id);
        return next;
      });
    }
  };

  const moveUp = async (idx: number): Promise<void> => {
    if (idx === 0) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    await reorderCustomFieldDefs(projectId, next.map((f) => f.id));
  };

  const moveDown = async (idx: number): Promise<void> => {
    if (idx >= items.length - 1) return;
    const next = [...items];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    await reorderCustomFieldDefs(projectId, next.map((f) => f.id));
  };

  const addNewChoice = (): void => {
    const trimmed = newChoiceInput.trim();
    if (!trimmed || newChoices.includes(trimmed)) return;
    setNewChoices((prev) => [...prev, trimmed]);
    setNewChoiceInput("");
  };

  const removeNewChoice = (choice: string): void => {
    setNewChoices((prev) => prev.filter((c) => c !== choice));
  };

  const addEditChoice = (id: number, choiceInput: string): void => {
    const trimmed = choiceInput.trim();
    if (!trimmed) return;
    setDraft(id, {
      choices: [...(editing.get(id)?.choices ?? []).filter((c) => c !== trimmed), trimmed],
    });
  };

  const removeEditChoice = (id: number, choice: string): void => {
    setDraft(id, {
      choices: (editing.get(id)?.choices ?? []).filter((c) => c !== choice),
    });
  };

  const resetNewForm = (): void => {
    setNewLabel("");
    setNewType("text");
    setNewRequired(false);
    setNewChoices([]);
    setNewChoiceInput("");
    setNewWorkItemTypeId(null);
  };

  const submitNew = async (): Promise<void> => {
    const label = newLabel.trim();
    if (!label) return;
    // Derive key from label: lowercase, replace non-alphanum with _, de-dup underscores, trim
    const key = label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60) || "field";
    setCreating(true);
    try {
      const options =
        NEEDS_CHOICES.has(newType) && newChoices.length > 0
          ? { choices: newChoices }
          : undefined;
      await addCustomField(projectId, {
        key,
        label,
        type: newType,
        required: newRequired,
        options,
        work_item_type_id: newWorkItemTypeId,
      });
      resetNewForm();
      setShowNew(false);
      toastSuccess("Feld angelegt.");
    } catch {
      // api wrapper already toasted
    } finally {
      setCreating(false);
    }
  };

  // ---- render ----

  return (
    <div class="flex flex-col gap-2">
      <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        Eigene Felder
      </div>

      {loading && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      )}

      {error && (
        <p class="text-xs text-[color:var(--color-error)]">Fehler: {error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine eigenen Felder.
        </p>
      )}

      {!loading &&
        items.map((f, idx) => {
          const draft = editing.get(f.id);
          const isSaving = saving.has(f.id);
          const isConfirmDel = confirmDel.has(f.id);

          return (
            <div
              key={f.id}
              class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden"
            >
              {/* Row header */}
              <div class="flex items-center gap-2 px-2 py-1.5">
                <span class="flex-1 text-xs font-medium text-[color:var(--color-on-surface)] truncate">
                  {draft ? draft.label || f.label : f.label}
                </span>

                {/* Type badge */}
                <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] rounded px-1.5 py-0.5">
                  {TYPE_LABEL[draft ? draft.type : f.type]}
                </span>

                {/* Required badge */}
                {(draft ? draft.required : f.required) && (
                  <span class="shrink-0 text-label text-[color:var(--color-primary)] bg-[color:var(--color-primary-container,var(--color-surface-container-low))] rounded px-1.5 py-0.5">
                    Pflicht
                  </span>
                )}

                {/* Type scope badge */}
                {(() => {
                  const scopeId = draft ? draft.workItemTypeId : f.work_item_type_id;
                  const scopeName = scopeId != null
                    ? (workItemTypes.find((t) => t.id === scopeId)?.name ?? `Typ ${scopeId}`)
                    : "Alle Typen";
                  return (
                    <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)] italic">
                      · {scopeName}
                    </span>
                  );
                })()}

                {/* Reorder + actions (edit role only) */}
                {canEdit && !draft && (
                  <div class="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => void moveUp(idx)}
                      disabled={idx === 0 || isSaving}
                      class="inline-flex size-6 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                      aria-label="Nach oben"
                    >
                      <ChevronUp size={14} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void moveDown(idx)}
                      disabled={idx >= items.length - 1 || isSaving}
                      class="inline-flex size-6 items-center justify-center rounded text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                      aria-label="Nach unten"
                    >
                      <ChevronDown size={14} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => startEdit(f)}
                      class="rounded px-1.5 py-0.5 text-label text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setConfirmDel((prev) => {
                          const next = new Set(prev);
                          next.add(f.id);
                          return next;
                        })
                      }
                      disabled={isSaving}
                      class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                      aria-label="Feld löschen"
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>

              {/* Inline editor */}
              {draft && canEdit && (
                <FieldEditor
                  draft={draft}
                  isSaving={isSaving}
                  workItemTypeOptions={workItemTypes}
                  onLabelChange={(v) => setDraft(f.id, { label: v })}
                  onTypeChange={(v) => setDraft(f.id, { type: v, choices: [] })}
                  onRequiredChange={(v) => setDraft(f.id, { required: v })}
                  onWorkItemTypeChange={(v) => setDraft(f.id, { workItemTypeId: v })}
                  onAddChoice={(ch) => addEditChoice(f.id, ch)}
                  onRemoveChoice={(ch) => removeEditChoice(f.id, ch)}
                  onCancel={() => cancelEdit(f.id)}
                  onSave={() => void commitEdit(f)}
                  saveLabel={isSaving ? "Speichere…" : "Speichern"}
                  saveDisabled={isSaving || !draft.label.trim()}
                />
              )}

              {/* Delete confirm */}
              {isConfirmDel && !draft && canEdit && (
                <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-1.5">
                  <p class="text-label text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-2 py-1.5">
                    Feld „{f.label}" wirklich löschen?
                  </p>
                  <div class="flex items-center gap-1.5 justify-end">
                    <button
                      type="button"
                      onClick={() =>
                        setConfirmDel((prev) => {
                          const next = new Set(prev);
                          next.delete(f.id);
                          return next;
                        })
                      }
                      class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                    >
                      Abbrechen
                    </button>
                    <Button
                      variant="destructive"
                      size="xs"
                      type="button"
                      onClick={() => void handleDelete(f)}
                      disabled={isSaving}
                    >
                      {isSaving ? "Lösche…" : "Löschen"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

      {/* Add new field */}
      {canEdit && (
        <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden">
          {!showNew ? (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              class="w-full inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] transition-colors"
            >
              <Plus size={14} aria-hidden="true" />
              Neues Feld anlegen
            </button>
          ) : (
            <div class="px-2 py-2 flex flex-col gap-2">
              <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                Neues Feld
              </div>
              <input
                type="text"
                value={newLabel}
                maxLength={100}
                onInput={(e) => setNewLabel((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void submitNew(); }
                  if (e.key === "Escape") { setShowNew(false); resetNewForm(); }
                }}
                placeholder="Bezeichnung (z.B. Kundennummer)"
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Bezeichnung des neuen Felds"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <select
                value={newType}
                onChange={(e) => {
                  setNewType((e.target as HTMLSelectElement).value as CustomFieldType);
                  setNewChoices([]);
                }}
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Feldtyp"
              >
                {CUSTOM_FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </select>

              {/* Choices editor for select/multiselect */}
              {NEEDS_CHOICES.has(newType) && (
                <ChoicesEditor
                  choices={newChoices}
                  inputValue={newChoiceInput}
                  onInputChange={setNewChoiceInput}
                  onAdd={addNewChoice}
                  onRemove={removeNewChoice}
                />
              )}

              <label class="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newRequired}
                  onChange={(e) =>
                    setNewRequired((e.target as HTMLInputElement).checked)
                  }
                  class="rounded border border-[color:var(--color-outline-variant)] text-[color:var(--color-primary)] focus-visible:ring-[color:var(--color-primary)]"
                />
                <span class="text-xs text-[color:var(--color-on-surface)]">Pflichtfeld</span>
              </label>

              <div class="flex flex-col gap-1">
                <label class="text-label text-[color:var(--color-on-surface-variant)]">
                  Gilt für Typ
                </label>
                <select
                  value={newWorkItemTypeId ?? ""}
                  onChange={(e) => {
                    const v = (e.target as HTMLSelectElement).value;
                    setNewWorkItemTypeId(v === "" ? null : Number(v));
                  }}
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                  aria-label="Gilt für Typ"
                >
                  <option value="">Alle Typen</option>
                  {workItemTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div class="flex items-center gap-1.5 justify-end">
                <button
                  type="button"
                  onClick={() => { setShowNew(false); resetNewForm(); }}
                  class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                >
                  Abbrechen
                </button>
                <Button
                  variant="primary"
                  size="xs"
                  type="button"
                  onClick={() => void submitNew()}
                  disabled={!newLabel.trim() || creating}
                >
                  {creating ? "Lege an…" : "Erstellen"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- sub-components ----------

interface ChoicesEditorProps {
  choices: string[];
  inputValue: string;
  onInputChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (choice: string) => void;
}

/** Inline chip-based editor for select/multiselect option choices. */
function ChoicesEditor(props: ChoicesEditorProps): preact.JSX.Element {
  const { choices, inputValue, onInputChange, onAdd, onRemove } = props;
  return (
    <div class="flex flex-col gap-1.5">
      <div class="text-label text-[color:var(--color-on-surface-variant)]">
        Optionen
      </div>
      {choices.length > 0 && (
        <div class="flex flex-wrap gap-1" role="list" aria-label="Optionen">
          {choices.map((c) => (
            <span
              key={c}
              role="listitem"
              class="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-surface-container-low)] border border-[color:var(--color-outline-variant)] px-2 py-0.5 text-micro text-[color:var(--color-on-surface)]"
            >
              {c}
              <button
                type="button"
                onClick={() => onRemove(c)}
                class="rounded-full p-0.5 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-error)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                aria-label={`Option „${c}" entfernen`}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div class="flex items-center gap-1">
        <input
          type="text"
          value={inputValue}
          maxLength={200}
          onInput={(e) => onInputChange((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onAdd(); }
          }}
          placeholder="Option hinzufügen…"
          class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
          aria-label="Neue Option"
        />
        <button
          type="button"
          onClick={onAdd}
          disabled={!inputValue.trim()}
          class="rounded bg-[color:var(--color-surface-container-low)] border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container)] disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
          aria-label="Option hinzufügen"
        >
          <Plus size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

interface EditChoicesWrapperProps {
  choices: string[];
  onAdd: (ch: string) => void;
  onRemove: (ch: string) => void;
}

/** Wrapper that holds its own local input state for the inline edit form. */
function EditChoicesWrapper(props: EditChoicesWrapperProps): preact.JSX.Element {
  const { choices, onAdd, onRemove } = props;
  const [inputValue, setInputValue] = useState("");
  const handleAdd = (): void => {
    onAdd(inputValue);
    setInputValue("");
  };
  return (
    <ChoicesEditor
      choices={choices}
      inputValue={inputValue}
      onInputChange={setInputValue}
      onAdd={handleAdd}
      onRemove={onRemove}
    />
  );
}

interface FieldEditorProps {
  draft: EditState;
  isSaving: boolean;
  workItemTypeOptions: Array<{ id: number; name: string }>;
  onLabelChange: (v: string) => void;
  onTypeChange: (v: CustomFieldType) => void;
  onRequiredChange: (v: boolean) => void;
  onWorkItemTypeChange: (v: number | null) => void;
  onAddChoice: (ch: string) => void;
  onRemoveChoice: (ch: string) => void;
  onCancel: () => void;
  onSave: () => void;
  saveLabel: string;
  saveDisabled: boolean;
}

/** Inline edit form for an existing field definition. */
function FieldEditor(props: FieldEditorProps): preact.JSX.Element {
  const {
    draft,
    isSaving,
    workItemTypeOptions,
    onLabelChange,
    onTypeChange,
    onRequiredChange,
    onWorkItemTypeChange,
    onAddChoice,
    onRemoveChoice,
    onCancel,
    onSave,
    saveLabel,
    saveDisabled,
  } = props;

  return (
    <div class="border-t border-[color:var(--color-outline-variant)] px-2 pb-2 pt-2 flex flex-col gap-2">
      <input
        type="text"
        value={draft.label}
        maxLength={100}
        onInput={(e) => onLabelChange((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); onSave(); }
          if (e.key === "Escape") onCancel();
        }}
        placeholder="Bezeichnung"
        class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
        aria-label="Bezeichnung"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
      <select
        value={draft.type}
        onChange={(e) =>
          onTypeChange((e.target as HTMLSelectElement).value as CustomFieldType)
        }
        class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
        aria-label="Feldtyp"
      >
        {CUSTOM_FIELD_TYPES.map((t) => (
          <option key={t} value={t}>
            {TYPE_LABEL[t]}
          </option>
        ))}
      </select>

      {NEEDS_CHOICES.has(draft.type) && (
        <EditChoicesWrapper
          choices={draft.choices}
          onAdd={onAddChoice}
          onRemove={onRemoveChoice}
        />
      )}

      <label class="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={draft.required}
          onChange={(e) => onRequiredChange((e.target as HTMLInputElement).checked)}
          class="rounded border border-[color:var(--color-outline-variant)] text-[color:var(--color-primary)] focus-visible:ring-[color:var(--color-primary)]"
        />
        <span class="text-xs text-[color:var(--color-on-surface)]">Pflichtfeld</span>
      </label>

      <div class="flex flex-col gap-1">
        <label class="text-label text-[color:var(--color-on-surface-variant)]">
          Gilt für Typ
        </label>
        <select
          value={draft.workItemTypeId ?? ""}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            onWorkItemTypeChange(v === "" ? null : Number(v));
          }}
          disabled={isSaving}
          class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-50"
          aria-label="Gilt für Typ"
        >
          <option value="">Alle Typen</option>
          {workItemTypeOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div class="flex items-center gap-1.5 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
        >
          Abbrechen
        </button>
        <Button
          variant="primary"
          size="xs"
          type="button"
          onClick={onSave}
          disabled={saveDisabled}
        >
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}
